import React, { useState } from "react";
import { Tabs, Table, Collapse, Typography, Divider, Tag, message } from "antd";
import { Link } from "react-router-dom";
import Hero1 from "../assets/img/hero_1.webp";
import Hero2 from "../assets/img/hero_2.webp";
import Hero3 from "../assets/img/hero_3.webp";
import Footer from "../components/Footer";
import HomeFAQ from "../components/home/faq";
import HomeNumbers from "../components/home/numbers";
import HomeTestimonials from "../components/home/testimonials";
import HomePricing from "../components/home/pricing";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
} from "../components/ui/carousel";
import { Button } from "../components/ui/button";
import Autoplay from "embla-carousel-autoplay";
import homeWork from "../assets/img/home_work.webp";
import "./Home.css";
import SDKDownloadModal from "../components/SDKDownloadModal";
import useUserStore from "../store/userStore";
import { useNavigate } from "react-router-dom";
import { apiService } from "../api/apiService";
import { useTranslation } from "../i18n/useTranslation";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const Home = () => {
  const { isLoggedIn } = useUserStore();
  const { t } = useTranslation();

  const [isSdkModalVisible, setIsSdkModalVisible] = useState(false);
  const navigate = useNavigate();

  const handleDownloadSDK = () => {
    if (!isLoggedIn) {
      navigate(`/login?redirect=${encodeURIComponent("/api-doc")}`);
      return;
    }
    setIsSdkModalVisible(true);
  };

  const handleModalClose = () => {
    setIsSdkModalVisible(false);
  };

  const handleSdkDownload = async (platform) => {
    if (platform !== "Linux") {
      message.info(t("homeSDKNotAvailable", { platform }));
      return;
    }

    try {
      const response = await apiService.getSdkDownloadLink();
      const downloadUrl = response?.data?.download_url;

      if (!response?.success || !downloadUrl) {
        throw new Error(response?.message || t("sdkDownloadLinkMissing"));
      }

      window.open(downloadUrl, "_blank", "noopener");
      message.success(t("homeSDKDownloadSuccess", { platform }));
    } catch (error) {
      console.error("SDK下载失败:", error);
      if (error?.response?.status === 401) {
        navigate(`/login?redirect=${encodeURIComponent("/api-doc")}`);
        return;
      }
      const errorMessage =
        error?.response?.data?.message || error.message || t("homeSDKRetryLater");
      message.error(t("homeSDKDownloadError", { error: errorMessage }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 英雄区 */}
      <section className="bg-gradient-to-b from-blue-300 to-indigo-100 py-16 pt-20 pb-10">
        <Carousel
          className="w-full"
          opts={{ loop: true, dots: true }}
          plugins={[
            Autoplay({
              playOnInit: true,
              delay: 5000,
              // stopOnMouseEnter: true,
              stopOnInteraction: false,
            }),
          ]}
        >
          <CarouselContent>
            {/* 轮播卡片 1 */}
            <CarouselItem>
              <div className="container mx-auto px-4 pb-4">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    <Title level={1} className="mb-4">
                      {t("homeHero1Title")}
                    </Title>
                    <div className="text-gray-600 py-6 text-base">
                      {t("homeHero1Desc")}
                    </div>
                    <div className="flex gap-4 mt-2 mb-6">
                      <Button
                        variant="blue-griadient"
                        size="lg"
                        onClick={handleDownloadSDK}
                      >
                        {t("homeHero1Button1")}
                      </Button>
                      <Link to="/api-doc">
                        <Button variant="secondary" size="lg">
                          {t("homeHero1Button2")}
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div className="md:w-1/2 max-w-[400px]">
                    <img src={Hero3} />
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* 轮播卡片 2 */}
            <CarouselItem>
              <div className="container mx-auto px-4 pb-4">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    <Title level={1} className="mb-4">
                      {t("homeHero2Title")}
                    </Title>
                    <div className="text-gray-600 py-6 text-base">
                      {t("homeHero2Desc")}
                    </div>
                    <div className="flex gap-4 mt-2 mb-6">
                      <Link to="/technology#core-technology">
                        <Button variant="blue-griadient" size="lg">
                          {t("homeHero2Button1")}
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div className="md:w-1/2 max-w-[400px]">
                    <img src={Hero1} />
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* 轮播卡片 3 */}
            <CarouselItem>
              <div className="container mx-auto px-4 pb-4">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    <Title level={1} className="mb-4">
                      {t("homeHero3Title")}
                    </Title>
                    <div className="text-gray-600 py-6 text-base">
                      {t("homeHero3Desc")}
                    </div>
                    <div className="flex gap-4 mt-2 mb-6">
                      <Link to="/solutions#smart-solutions">
                        <Button variant="blue-griadient" size="lg">
                          {t("homeHero3Button1")}
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div className="md:w-1/2 max-w-[400px]">
                    <img src={Hero2} />
                  </div>
                </div>
              </div>
            </CarouselItem>
          </CarouselContent>
          <CarouselDots />
        </Carousel>
      </section>

      <section
        className="py-24 md:pb-32 bg-white bg-center"
        style={{ backgroundImage: "url('/img/pattern-white.svg')" }}
      >
        <div className="container ">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100  uppercase rounded-full shadow-sm">
              {t("homeCoreFeaturesTag")}
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              {t("homeCoreFeaturesTitle")}
            </h2>
            <p className="text-base md:text-lg text-slate-500">
              {t("homeCoreFeaturesDesc")}
            </p>
          </div>
          <div className="flex flex-wrap -mx-4 text-slate-800">
            <div className="w-full md:w-1/2 lg:w-1/3 px-4">
              <div className="h-full p-8 text-center hover:bg-white rounded-md hover:shadow-xl transition duration-200">
                <div className="gif-container" aria-hidden="true">
                  <img src="/video/littleDogLearnWalking.gif" alt={t("homeCoreFeature1VideoIntro")} />
                </div>
                <p className="text-slate-500 ">
                  {t("homeCoreFeature1VideoIntro")}
                </p>
              </div>
            </div>
            <div className="w-full md:w-1/2 lg:w-1/3 px-4">
              <div className="h-full p-8 text-center hover:bg-white rounded-md hover:shadow-xl transition duration-200">
                <div className="gif-container" aria-hidden="true">
                  <img src="/video/puppyPiLearnTrackingTarget.gif" alt={t("homeCoreFeature2VideoIntro")} />
                </div>
                <p className="text-slate-500 ">
                  {t("homeCoreFeature2VideoIntro")}
                </p>
              </div>
            </div>
            <div className="w-full md:w-1/2 lg:w-1/3 px-4">
              <div className="h-full p-8 text-center hover:bg-white rounded-md hover:shadow-xl transition duration-200">
                <div className="gif-container" aria-hidden="true">
                  <img src="/video/robotVillainLearnWalking.gif" alt={t("homeCoreFeature3VideoIntro")} />
                </div>
                <p className="text-slate-500 ">
                  {t("homeCoreFeature3VideoIntro")}
                </p>
              </div>
            </div>
            <div className="w-full md:w-1/2 lg:w-1/3 px-4">
              <div className="h-full p-8 text-center hover:bg-white rounded-md hover:shadow-xl transition duration-200">
                <div className="gif-container" aria-hidden="true">
                  <img src="/video/carAvoidsLightMovement.gif" alt={t("homeCoreFeature4VideoIntro")} />
                </div>
                <p className="text-slate-500 ">
                  {t("homeCoreFeature4VideoIntro")}
                </p>
              </div>
            </div>
            <div className="w-full md:w-1/2 lg:w-1/3 px-4">
              <div className="h-full p-8 text-center hover:bg-white rounded-md hover:shadow-xl transition duration-200">
                <div className="gif-container" aria-hidden="true">
                  <img src="/video/carLightDrivingSports.gif" alt={t("homeCoreFeature5VideoIntro")} />
                </div>
                <p className="text-slate-500 ">
                  {t("homeCoreFeature5VideoIntro")}
                </p>
              </div>
            </div>
            <div className="w-full md:w-1/2 lg:w-1/3 px-4">
              <div className="h-full p-8 text-center hover:bg-white rounded-md hover:shadow-xl transition duration-200">
                <div className="gif-container" aria-hidden="true">
                  <img src="/video/carFollowPerson'sMovement.gif" alt={t("homeCoreFeature6VideoIntro")} />
                </div>
                <p className="text-slate-500 ">
                  {t("homeCoreFeature6VideoIntro")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="py-24 bg-white overflow-hidden bg-center"
        style={{ backgroundImage: "url('/img/pattern-white.svg')" }}
      >
        <div className="container ">
          <div className="flex flex-wrap -mx-4">
            <div className="w-full md:w-1/2 px-4 mb-16 md:mb-0">
              <div className="relative mx-auto md:ml-0 max-w-max">
                <img
                  className="absolute z-10 -right-8 -top-8 w-28 md:w-auto"
                  src="/img/circle3-yellow.svg"
                  alt=""
                  width={129}
                  height={129}
                />

                <img
                  className="absolute z-10 -left-10 -bottom-8 w-28 md:w-auto"
                  src="/img/dots3-blue.svg"
                  alt=""
                  width={149}
                  height={91}
                />

                <img src={homeWork} alt="" width={456} height={456} />
              </div>
            </div>
            <div className="w-full md:w-1/2 px-4">
              <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100  uppercase rounded-full shadow-sm">
                {t("homeQuickStartTag")}
              </span>
              <h2 className="mb-12 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
                {t("homeQuickStartTitle")}
              </h2>
              <div className="flex flex-wrap -mx-4 text-center md:text-left text-slate-800">
                <div className="w-full md:w-1/2 px-4 mb-8">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    1
                  </div>
                  <h3 className="mb-2 text-base md:text-lg font-bold">
                    {t("homeQuickStartStep1Title")}
                  </h3>
                  <p className=" text-slate-500">
                    {t("homeQuickStartStep1Desc")}
                  </p>
                </div>
                <div className="w-full md:w-1/2 px-4 mb-8">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    2
                  </div>
                  <h3 className="mb-2 text-base md:text-lg font-bold">
                    {t("homeQuickStartStep2Title")}
                  </h3>
                  <p className=" text-slate-500">
                    {t("homeQuickStartStep2Desc")}
                  </p>
                </div>
                <div className="w-full md:w-1/2 px-4 mb-8">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    3
                  </div>
                  <h3 className="mb-2 text-base md:text-lg font-bold">
                    {t("homeQuickStartStep3Title")}
                  </h3>
                  <p className=" text-slate-500">
                    {t("homeQuickStartStep3Desc")}
                  </p>
                </div>
                <div className="w-full md:w-1/2 px-4">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    4
                  </div>
                  <h3 className="mb-2 text-base md:text-lg font-bold">
                    {t("homeQuickStartStep4Title")}
                  </h3>
                  <p className=" text-slate-500">
                    {t("homeQuickStartStep4Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HomeNumbers />

      <HomePricing />

      <HomeTestimonials />

      <HomeFAQ />

      {/* <FreeTrial /> */}

      <Footer />

      {/* SDK下载弹窗 */}
      <SDKDownloadModal
        visible={isSdkModalVisible}
        onClose={handleModalClose}
        onDownload={handleSdkDownload}
      />

    </div>
  );
};

export default Home;
